import { useState } from "react";
import { Calendar as CalendarIcon, ChevronDown, Clock, Search } from "lucide-react";
import { fr, de, enUS } from "date-fns/locale";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useBiz } from "@/lib/businessI18n";
import { inputCls } from "./ui";

const LOCALES = { fr, en: enUS, de };

const toDate = (iso) => {
  if (!iso) return undefined;
  const [y, m, d] = iso.split("-").map(Number);
  return y && m && d ? new Date(y, m - 1, d) : undefined;
};
const toIso = (date) =>
  date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}` : "";
const display = (iso) => (iso ? iso.split("-").reverse().join(".") : "");

export function DateField({ value, onChange, placeholder, className = "", testId }) {
  const { lang } = useBiz();
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className={`${inputCls} ${className} flex items-center justify-between gap-2 text-left ${value ? "" : "text-slate/60"}`}>
          <span>{display(value) || placeholder || "JJ.MM.AAAA"}</span>
          <CalendarIcon className="h-4 w-4 shrink-0 text-slate" strokeWidth={1.5} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto rounded-2xl border-ink/10 p-0">
        <Calendar mode="single" locale={LOCALES[lang] || fr} selected={toDate(value)}
          onSelect={(d) => { if (d) { onChange(toIso(d)); setOpen(false); } }}
          classNames={{
            day_selected: "bg-miles text-white hover:bg-miles focus:bg-miles rounded-md",
            day_today: "bg-miles-ice text-miles rounded-md",
          }} />
      </PopoverContent>
    </Popover>
  );
}

const HOURS = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));

export function TimeField({ value, onChange, className = "", testId }) {
  const [open, setOpen] = useState(false);
  const [h, m] = (value || "").split(":");
  const pick = (nh, nm) => {
    const next = `${nh ?? h ?? "12"}:${nm ?? m ?? "00"}`;
    onChange(next);
    if (nm) setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className={`${inputCls} ${className} flex items-center justify-between gap-2 text-left ${value ? "" : "text-slate/60"}`}>
          <span>{value || "--:--"}</span>
          <Clock className="h-4 w-4 shrink-0 text-slate" strokeWidth={1.5} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto rounded-2xl border-ink/10 p-2">
        <div className="flex gap-1">
          <div className="max-h-48 overflow-y-auto pr-1">
            {HOURS.map((hh) => (
              <button key={hh} type="button" onClick={() => pick(hh, undefined)} data-testid={testId ? `${testId}-h${hh}` : undefined}
                className={`block w-12 rounded-lg px-2 py-1 text-center text-sm ${hh === h ? "bg-miles font-semibold text-white" : "text-ink hover:bg-miles-ice"}`}>{hh}</button>
            ))}
          </div>
          <div className="max-h-48 overflow-y-auto">
            {MINUTES.map((mm) => (
              <button key={mm} type="button" onClick={() => pick(undefined, mm)} data-testid={testId ? `${testId}-m${mm}` : undefined}
                className={`block w-12 rounded-lg px-2 py-1 text-center text-sm ${mm === m ? "bg-miles font-semibold text-white" : "text-ink hover:bg-miles-ice"}`}>{mm}</button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}


// Annuaire des voyageurs avec recherche + groupes alphabétiques (A, B, C…)
export function GuestPicker({ guests, onPick, placeholder, className = "", testId = "guest-picker" }) {
  const { t } = useBiz();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const norm = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const person = (g) => `${g.first_name || ""} ${g.last_name || ""}`.trim();
  const sortKey = (g) => norm(g.first_name || g.last_name || g.company);
  const filtered = (guests || [])
    .filter((g) => !q || norm(`${person(g)} ${g.company || ""} ${g.email || ""} ${g.phone || ""}`).includes(norm(q)))
    .sort((a, b) => sortKey(a).localeCompare(sortKey(b), "fr"));
  const groups = filtered.reduce((acc, g) => {
    const letter = (sortKey(g)[0] || "#").toUpperCase();
    (acc[letter] = acc[letter] || []).push(g);
    return acc;
  }, {});
  const letters = Object.keys(groups).sort((a, b) => a.localeCompare(b, "fr"));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className={`${inputCls} ${className} flex items-center justify-between gap-2 text-left text-slate`}>
          <span className="truncate">{placeholder || t("mb.search_guest")}</span>
          <ChevronDown className="h-4 w-4 shrink-0" strokeWidth={1.5} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] rounded-2xl border-ink/10 p-0">
        <div className="border-b border-ink/[0.06] p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate" strokeWidth={1.5} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("mb.search_guest")}
              data-testid={`${testId}-search`} className={`${inputCls} h-10 pl-9`} />
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto p-1.5">
          {letters.map((letter) => (
            <div key={letter}>
              <p className="px-2.5 pb-1 pt-2 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate"
                data-testid={`${testId}-group-${letter}`}>{letter}</p>
              {groups[letter].map((g) => (
                <button key={g.id} type="button" data-testid={`${testId}-option-${g.id}`}
                  onClick={() => { onPick(g); setOpen(false); setQ(""); }}
                  className="flex w-full items-center justify-between gap-2 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-cloud">
                  <span className="text-sm font-medium text-ink">{person(g)}</span>
                  <span className="truncate text-xs text-slate">{[g.company, g.phone || g.email].filter(Boolean).join(" · ")}</span>
                </button>
              ))}
            </div>
          ))}
          {letters.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-slate" data-testid={`${testId}-empty`}>{t("guests.empty")}</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
